import { useMemo, useState } from 'react';
import { Panel, CountKpi } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';
import { count } from '../lib/format.js';

interface AuditEvent {
  id: string; occurredUtc: string;
  actor: string | null; action: string;
  platform: string | null; objectType: string | null;
  objectId: string | null; reason: string | null;
}

interface AuditResponse {
  events: AuditEvent[];
  limit: number;
  generatedUtc: string;
}

const ALL = '__all__';

/** Actions that changed something outside this system get called out. */
function isExternalWrite(action: string): boolean {
  return /MUTATE|CREATE|UPDATE|DELETE|PUBLISH|ENABLE|PAUSE|REVOK/i.test(action);
}

export function Audit({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data, error } = useApi<AuditResponse>('/api/audit?limit=300', getToken);
  const [action, setAction] = useState(ALL);

  const actions = useMemo(
    () => Array.from(new Set((data?.events ?? []).map((e) => e.action))).sort(),
    [data],
  );

  if (error) return <EmptyState title="Could not load the audit trail" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading the audit trail.</EmptyState>;

  const rows = data.events.filter((e) => action === ALL || e.action === action);
  const writes = data.events.filter((e) => isExternalWrite(e.action));
  const actors = new Set(data.events.map((e) => e.actor).filter((a): a is string => a !== null));

  return (
    <div className="stack">
      <Panel
        title="Accountability trail"
        subtitle="Every write to an advertising platform, every approval and every credential change is recorded here. This is the record that answers 'who changed what, when, and why'."
      >
        <div className="kpi-grid">
          <CountKpi label="Events held" value={data.events.length} note={`Most recent ${data.limit}`} />
          <CountKpi label="External writes" value={writes.length > 0 ? writes.length : null} note="Changes made outside this system" />
          <CountKpi label="Distinct actors" value={actors.size > 0 ? actors.size : null} />
          <CountKpi label="Distinct actions" value={actions.length} />
        </div>
      </Panel>

      <Panel title="Events" subtitle="Newest first. The trail is append-only — entries are never edited or removed.">
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <select className="select" value={action} onChange={(e) => setAction(e.target.value)}>
            <option value={ALL}>All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
          </select>
          <span style={{ alignSelf: 'center', fontSize: 12, color: 'var(--text-faint)' }}>
            {count(rows.length)} of {count(data.events.length)} events
          </span>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th>When (UTC)</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Object</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12, whiteSpace: 'nowrap' }}>
                  {e.occurredUtc.replace('T', ' ').slice(0, 19)}
                </td>
                <td>
                  <span className={isExternalWrite(e.action) ? 'pill warn' : 'pill'}>
                    {e.action.replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={{ color: e.actor === null ? 'var(--text-faint)' : undefined, fontSize: 12 }}>
                  {e.actor ?? 'system'}
                </td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  {e.objectType ?? '—'}
                  {e.objectId ? <span style={{ color: 'var(--text-faint)' }}> · {e.objectId}</span> : null}
                </td>
                <td style={{ color: e.reason === null ? 'var(--text-faint)' : 'var(--text-muted)', fontSize: 12 }}>
                  {e.reason ?? '—'}
                </td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr><td colSpan={5} style={{ color: 'var(--text-faint)' }}>No events match this filter.</td></tr>
            ) : null}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
